import type { SessionOutcome } from '@bettercomp/ams-spa-auth';
import { isApiError } from 'common/types';
import { isAppAccessDenied } from 'service/accessDenied';

// The two outcomes a failed identity load ends in; AmsAuthProvider hands the
// gate one of them in place of the minted session outcome.
export const IDENTITY_REFUSED = {
  kind: 'terminal',
  source: 'host',
  code: 'identity_refused',
} as const satisfies SessionOutcome;

export const IDENTITY_UNAVAILABLE = {
  kind: 'unavailable',
  source: 'host',
  code: 'identity_unavailable',
} as const satisfies SessionOutcome;

export type HostOutcome = typeof IDENTITY_REFUSED | typeof IDENTITY_UNAVAILABLE;

/** AMS signed the person in, and only the api identity load after it failed. */
export const isIdentityUnavailable = (outcome: SessionOutcome | undefined): boolean =>
  outcome?.kind === 'unavailable' && outcome.source === 'host';

/**
 * The session holds a usable AMS token: the app opened, or only the api identity load failed.
 * AMS calls (the switcher lists, the switches) work in both, because AMS never reads MP rows.
 */
export const holdsAmsToken = (outcome: SessionOutcome | undefined): boolean =>
  outcome?.kind === 'minted' || isIdentityUnavailable(outcome);

/**
 * Only the api's app-access refusal is a verdict about the account. A 401 that
 * survived renew-then-retry means the api could not establish the identity
 * (provisioning gap), and 5xx, network or an uncoded 403 say nothing about the
 * person: all of those are the identity being unavailable, with retry.
 */
export const classifyIdentityLoadFailure = (error: unknown): HostOutcome =>
  isApiError(error) && isAppAccessDenied(error.status, error.body) ? IDENTITY_REFUSED : IDENTITY_UNAVAILABLE;

/** Status and errorCode only: never the body message, never the email. */
export const describeIdentityLoadFailure = (error: unknown): string => {
  if (isApiError(error)) {
    const errorCode = (error.body as { errorCode?: unknown } | undefined)?.errorCode;
    return typeof errorCode === 'string' ? `${error.status} ${errorCode}` : String(error.status);
  }
  return error instanceof Error ? error.name : 'unknown';
};
